import React, { forwardRef, useState, useRef } from 'react';
import { 
  TextField, 
  InputAdornment, 
  IconButton,
  FormControl
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';

/**
 * Input component using Material-UI TextField
 * This component provides a consistent interface for text inputs with validation
 */
const MuiInput = forwardRef(({
  type = 'text',
  label,
  placeholder,
  error,
  helperText,
  required = false,
  disabled = false,
  icon,
  fullWidth = true,
  size = 'medium',
  id,
  name,
  value,
  defaultValue,
  onChange,
  onBlur,
  onFocus,
  sx = {},
  ...props
}, ref) => {
  const [showPassword, setShowPassword] = useState(false);
  const idRef = useRef(id || `input-${Math.random().toString(36).substring(2, 9)}`);
  
  // Use stable ID
  const inputId = idRef.current;
  
  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  // Determine the input type for password fields
  const inputType = type === 'password' && showPassword ? 'text' : type;

  // Create start adornment if icon is provided
  const startAdornment = icon ? (
    <InputAdornment position="start">
      {icon}
    </InputAdornment>
  ) : null;

  // Create end adornment for password fields
  const endAdornment = type === 'password' ? (
    <InputAdornment position="end">
      <IconButton
        aria-label={showPassword ? 'Hide password' : 'Show password'}
        onClick={togglePasswordVisibility}
        edge="end"
        size={size === 'small' ? 'small' : 'medium'}
      >
        {showPassword ? <VisibilityOff /> : <Visibility />}
      </IconButton>
    </InputAdornment>
  ) : null;

  return (
    <FormControl 
      fullWidth={fullWidth} 
      error={Boolean(error)}
      disabled={disabled}
      sx={{ mb: 2, ...sx }}
    >
      <TextField
        inputRef={ref}
        id={inputId}
        name={name}
        type={inputType}
        label={label}
        placeholder={placeholder}
        required={required}
        disabled={disabled}
        error={Boolean(error)}
        helperText={error || helperText}
        value={value}
        defaultValue={defaultValue}
        onChange={onChange}
        onBlur={onBlur}
        onFocus={onFocus}
        size={size}
        fullWidth={fullWidth}
        InputProps={{
          startAdornment: startAdornment,
          endAdornment: endAdornment,
        }}
        FormHelperTextProps={{
          error: Boolean(error),
        }}
        {...props}
      />
    </FormControl>
  );
});

MuiInput.displayName = 'MuiInput';

export default MuiInput;